import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, Send, Paperclip, Mic, ImagePlus, Camera, CheckCircle2, ChevronRight, MapPin, Clock, Truck, Users, Shield, Loader2, Navigation } from 'lucide-react';
import { detectServiceFromPrompt, getServiceConfig, setActiveService, ServiceId } from '../lib/serviceData';
import { saveConversation } from '../lib/coPilotStore';
import { getSlotsForService, parseInitialPrompt, parseUserReply, nextMissingSlot, allRequiredFilled, formatAcknowledgement, getInitialGreeting, getCompletionMessage, Slot, SlotValues, ConversationTurn } from '../lib/agenticAgent';
import { recordSearch } from '../lib/activityStore';
// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
export function BookingFlow() {
  const location = useLocation();
  const navigate = useNavigate();
  // Resolve service & prompt
  const stateService = location.state?.service as ServiceId | undefined;
  const statePrompt = location.state?.prompt as string | undefined;
  const resolvedServiceId: ServiceId = stateService || (statePrompt ? detectServiceFromPrompt(statePrompt) : undefined) || (typeof window !== 'undefined' ? sessionStorage.getItem('pair2.activeService') as ServiceId || 'home' : 'home');
  const service = useMemo(() => getServiceConfig(resolvedServiceId), [resolvedServiceId]);
  const slots = useMemo(() => getSlotsForService(resolvedServiceId), [resolvedServiceId]);
  const initialPrompt = statePrompt || service.defaultPrompt;
  // Whether the user came from the Home AI handoff overlay (no loading needed)
  const preWarmed = location.state?.preWarmed === true;
  // Conversation state
  const [thinking, setThinking] = useState(!preWarmed);
  const [turns, setTurns] = useState<ConversationTurn[]>([]);
  const [values, setValues] = useState<SlotValues>({});
  const [activeSlot, setActiveSlot] = useState<Slot | null>(null);
  const [input, setInput] = useState('');
  const [isDone, setIsDone] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);
  const turnIdRef = useRef(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Persist active service for the rest of the app (results, widget, etc.)
  // and log the search into the user's search history for later resumption.
  useEffect(() => {
    setActiveService(service.id, initialPrompt);
    recordSearch({
      prompt: initialPrompt,
      serviceId: service.id,
      serviceName: service.name
    });
  }, [service.id, initialPrompt, service.name]);
  // Boot the conversation: parse the initial prompt, then ask for the first missing slot.
  useEffect(() => {
    const prefilled = parseInitialPrompt(initialPrompt, service.id);
    // Honor a "bonus" second location parsed out (e.g. "Marina to Downtown" pre-fills from + to)
    const cleaned: SlotValues = {
      ...prefilled
    };
    const buildInitialTurns = () => {
      // 1. user's original prompt (echo it back as the first user turn)
      const userTurn: ConversationTurn = {
        id: turnIdRef.current++,
        role: 'user',
        text: initialPrompt
      };
      // 2. AI greeting (acknowledges what we caught)
      const greetingText = getInitialGreeting(service.id, initialPrompt, cleaned, slots);
      // 3. Find next missing slot and ask for it
      const next = nextMissingSlot(cleaned, slots);
      let aiText = greetingText;
      let suggestions: string[] | undefined;
      let showUploader = false;
      let isComplete = false;
      if (next) {
        aiText += `\n\n${next.askPhrases[0]}`;
        suggestions = next.suggestions;
        showUploader = next.kind === 'photos';
      } else {
        aiText = getCompletionMessage(service.id);
        isComplete = true;
      }
      const aiTurn: ConversationTurn = {
        id: turnIdRef.current++,
        role: 'ai',
        text: aiText,
        suggestions,
        showUploader,
        isComplete
      };
      setValues(cleaned);
      setTurns([userTurn, aiTurn]);
      setActiveSlot(next);
      setThinking(false);
      if (!next) setIsDone(true);
    };
    // If we came from the AI handoff overlay, the user already saw a thinking
    // animation on the previous page — render the conversation instantly.
    if (preWarmed) {
      buildInitialTurns();
      return;
    }
    const t1 = setTimeout(buildInitialTurns, 1300);
    return () => clearTimeout(t1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service.id]);
  // Auto-scroll the chat on new turns
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, thinking]);
  // Submit handler — accepts free text OR a chip click
  const submitReply = (rawText: string) => {
    if (!rawText.trim() || !activeSlot) return;
    // Skip-photos shortcut — if the AI is currently asking about photos and
    // the user says "no", "skip", "no photos", etc., treat it as a skip.
    if (activeSlot.kind === 'photos') {
      const skipPattern = /^(no|nope|skip|none|no photo|no photos|no pictures|no pics|don'?t have|not now|later|pass)\b/i;
      if (skipPattern.test(rawText.trim())) {
        skipPhotos();
        return;
      }
    }
    // User turn
    const userTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'user',
      text: rawText
    };
    // Parse against the active slot
    const parsed = parseUserReply(rawText, activeSlot);
    const nextValues: SlotValues = {
      ...values,
      ...parsed
    };
    // Bonus parse: if the location slot reply also contained a second location, fill the next location slot.
    if ('__bonus_location__' in parsed) {
      const bonusValue = parsed['__bonus_location__'];
      delete nextValues['__bonus_location__'];
      const nextLocSlot = slots.find(s => s.kind === 'location' && s.id !== activeSlot.id && (nextValues[s.id] === undefined || nextValues[s.id] === ''));
      if (nextLocSlot) {
        nextValues[nextLocSlot.id] = bonusValue;
      }
    }
    setValues(nextValues);
    // AI ack + ask next
    const ackParts: string[] = [];
    if (parsed[activeSlot.id] !== undefined) {
      ackParts.push(formatAcknowledgement(activeSlot, parsed[activeSlot.id]));
    }
    if ('__bonus_location__' in parsed || nextValues['__bonus_caught__']) {
      // covered above
    }
    const nextSlot = nextMissingSlot(nextValues, slots);
    let aiText = ackParts.join(' ');
    let suggestions: string[] | undefined;
    let showUploader = false;
    let isComplete = false;
    if (nextSlot) {
      aiText += `\n\n${pickAsk(nextSlot)}`;
      suggestions = nextSlot.suggestions;
      showUploader = nextSlot.kind === 'photos';
    } else {
      aiText = `${ackParts.join(' ')}\n\n${getCompletionMessage(service.id)}`;
      isComplete = true;
    }
    const aiTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'ai',
      text: aiText,
      suggestions,
      showUploader,
      isComplete
    };
    setTurns(t => [...t, userTurn, aiTurn]);
    setActiveSlot(nextSlot);
    setInput('');
    if (!nextSlot) setIsDone(true);
  };
  const handleSendText = () => submitReply(input);
  // Simulate GPS resolution when the user picks "Use my current location".
  const simulateCurrentLocation = (): Promise<string> => {
    const here = ['Business Bay (current location)', 'Dubai Marina (current location)', 'Downtown Dubai (current location)'];
    return new Promise(resolve => {
      setTimeout(() => resolve(here[Math.floor(Math.random() * here.length)]), 700);
    });
  };
  const handleChip = (chip: string) => {
    if (chip.startsWith('📍')) {
      // Fake a brief GPS lookup, then submit the resolved address.
      setInput('📍 Locating you…');
      simulateCurrentLocation().then(addr => {
        setInput('');
        submitReply(addr);
      });
      return;
    }
    submitReply(chip);
  };
  // Dedicated "Share location from Google Maps" handler — shown as a button
  // whenever the AI is asking about a location slot.
  const [isSharingLocation, setIsSharingLocation] = useState(false);
  const handleShareLocation = () => {
    if (!activeSlot || activeSlot.kind !== 'location' || isSharingLocation) return;
    setIsSharingLocation(true);
    setInput('📍 Locating you via Google Maps…');
    simulateCurrentLocation().then(addr => {
      setInput('');
      setIsSharingLocation(false);
      submitReply(addr);
    });
  };
  const handlePhotoUpload = () => {
    if (!activeSlot || activeSlot.kind !== 'photos') return;
    const newCount = photoCount + 1;
    setPhotoCount(newCount);
    const userTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'user',
      attachments: [{
        name: `room_${newCount}.jpg`,
        url: `https://images.unsplash.com/photo-${newCount % 2 === 0 ? '1555041469-a586c61ea9bc' : '1583847268964-b28dc8f51f92'}?auto=format&fit=crop&w=240&h=180&q=70`
      }]
    };
    const photoFeedback = ['✓ Got it — detected a sofa and TV unit in the living room. Adding to inventory.', '✓ Captured. Detected a queen bed and wardrobe in the bedroom.', '✓ Inventory complete. Carriers will see your full item list.'][Math.min(newCount - 1, 2)];
    const nextValues: SlotValues = {
      ...values,
      [activeSlot.id]: [...(values[activeSlot.id] as string[] || []), userTurn.attachments![0].url]
    };
    setValues(nextValues);
    // If we have 2+ photos, treat the photos slot as satisfied and move on.
    let nextSlot: Slot | null = activeSlot;
    let aiText = photoFeedback;
    let suggestions: string[] | undefined = ['Add another photo', "That's enough"];
    let isComplete = false;
    if (newCount >= 2) {
      // Force-satisfy the photos slot and look ahead
      const valuesWithPhotosDone = {
        ...nextValues,
        [activeSlot.id]: nextValues[activeSlot.id] || ['done']
      };
      const upcoming = nextMissingSlot({
        ...valuesWithPhotosDone
      }, slots.filter(s => s.id !== activeSlot.id));
      nextSlot = upcoming;
      if (!upcoming) {
        aiText = `${photoFeedback}\n\n${getCompletionMessage(service.id)}`;
        suggestions = undefined;
        isComplete = true;
      } else {
        aiText = `${photoFeedback}\n\n${pickAsk(upcoming)}`;
        suggestions = upcoming.suggestions;
      }
    }
    const aiTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'ai',
      text: aiText,
      suggestions,
      showUploader: nextSlot?.kind === 'photos',
      isComplete
    };
    setTurns(t => [...t, userTurn, aiTurn]);
    setActiveSlot(nextSlot);
    if (!nextSlot) setIsDone(true);
  };
  const skipPhotos = () => {
    if (!activeSlot || activeSlot.kind !== 'photos') return;
    const userTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'user',
      text: 'Skip photos'
    };
    const remaining = slots.filter(s => s.id !== activeSlot.id);
    const upcoming = nextMissingSlot(values, remaining);
    let aiText: string;
    let isComplete = false;
    if (upcoming) {
      aiText = `No problem — I'll use a standard estimate. ${pickAsk(upcoming)}`;
    } else {
      aiText = `No problem — I'll use a standard estimate.\n\n${getCompletionMessage(service.id)}`;
      isComplete = true;
    }
    const aiTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'ai',
      text: aiText,
      suggestions: upcoming?.suggestions,
      showUploader: upcoming?.kind === 'photos',
      isComplete
    };
    setTurns(t => [...t, userTurn, aiTurn]);
    setActiveSlot(upcoming);
    if (!upcoming) setIsDone(true);
  };
  // -------------------------- UI --------------------------
  if (thinking) {
    return <div className="min-h-[calc(100vh-5rem)] flex flex-col items-center justify-center bg-surface-100" data-id="element-991">
        <motion.div animate={{
        scale: [1, 1.05, 1]
      }} transition={{
        repeat: Infinity,
        duration: 2
      }} className="w-24 h-24 rounded-3xl orange-gradient-bg flex items-center justify-center hero-glow mb-8" data-id="element-992">
          <Sparkles className="w-10 h-10 text-white" data-id="element-993" />
        </motion.div>
        <h2 className="text-2xl font-display font-bold text-gray-900 mb-2" data-id="element-994">
          Pair2 AI is analyzing your request...
        </h2>
        <p className="text-gray-500 text-base font-medium italic max-w-md text-center px-6" data-id="element-995">
          "{initialPrompt}"
        </p>
      </div>;
  }
  const requiredSlots = slots.filter(s => !s.optional);
  const filledRequiredCount = requiredSlots.filter(s => values[s.id] !== undefined && values[s.id] !== '').length;
  const progress = Math.round(filledRequiredCount / requiredSlots.length * 100);
  return <div className="min-h-[calc(100vh-5rem)] bg-surface-100" data-id="element-996">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-997">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-id="element-998">
          <div data-id="element-999">
            <div className="inline-flex items-center gap-2 bg-brand-50 text-brand-700 px-3 py-1 rounded-full text-xs font-semibold mb-2" data-id="element-1000">
              <Sparkles className="w-3.5 h-3.5" data-id="element-1001" />
              {service.name}
            </div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-gray-900" data-id="element-1002">
              {isDone ? 'Booking ready' : 'Tell me a bit more'}
            </h1>
            <p className="text-gray-500 mt-1 text-sm" data-id="element-1003">
              {isDone ? "I've prepared your booking summary — ready to find carriers." : "I'll gather what I need to match you with the best carrier."}
            </p>
          </div>
          {/* Progress */}
          <div className="flex items-center gap-3 bg-white border border-gray-100 rounded-2xl px-4 py-3 shadow-sm w-full sm:w-auto" data-id="element-1004">
            <div className="text-xs text-gray-500 font-medium" data-id="element-1005">
              {filledRequiredCount} / {requiredSlots.length} captured
            </div>
            <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1006">
              <motion.div className="h-full orange-gradient-bg" initial={{
              width: 0
            }} animate={{
              width: `${progress}%`
            }} transition={{
              type: 'spring',
              damping: 24,
              stiffness: 200
            }} data-id="element-1007" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-id="element-1008">
          {/* CHAT COLUMN */}
          <div className="lg:col-span-3" data-id="element-1009">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm flex flex-col h-[640px] overflow-hidden" data-id="element-1010">
              {/* Chat header */}
              <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3" data-id="element-1011">
                <div className="relative" data-id="element-1012">
                  <div className="w-10 h-10 rounded-full orange-gradient-bg flex items-center justify-center shadow-sm" data-id="element-1013">
                    <Sparkles className="w-5 h-5 text-white" data-id="element-1014" />
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-white rounded-full" data-id="element-1015" />
                </div>
                <div data-id="element-1016">
                  <p className="font-bold text-sm text-gray-900 leading-tight" data-id="element-1017">
                    Pair2 AI
                  </p>
                  <p className="text-[11px] text-gray-500 font-medium leading-tight" data-id="element-1018">
                    Conversational booking agent · {service.shortName}
                  </p>
                </div>
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4 bg-gradient-to-b from-brand-50/20 to-white" data-id="element-1019">
                {turns.map(turn => <TurnBubble key={turn.id} turn={turn} onChip={handleChip} onUpload={handlePhotoUpload} onSkipPhotos={skipPhotos} isLatest={turn.id === turns[turns.length - 1].id} data-id="element-1020" />)}
              </div>

              {/* Input */}
              <div className="border-t border-gray-100 p-3 bg-white" data-id="element-1021">
                <div className="flex items-center gap-2 bg-gray-50 rounded-2xl px-3 py-2 border border-gray-100 focus-within:border-brand-300 focus-within:bg-white transition-colors" data-id="element-1022">
                  <button onClick={handlePhotoUpload} disabled={!activeSlot} className="text-gray-400 hover:text-brand-500 transition-colors disabled:opacity-30" aria-label="Attach" data-id="element-1023">
                    <Paperclip className="w-4 h-4" data-id="element-1024" />
                  </button>
                  <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSendText();
                  }
                }} placeholder={isDone ? 'All set — find carriers on the right →' : activeSlot?.kind === 'location' ? 'Type the address, or share via Google Maps above' : 'Reply to Pair2 AI...'} disabled={isDone} className="flex-1 bg-transparent outline-none text-sm placeholder:text-gray-400 py-1 disabled:opacity-50" data-id="element-1025" />
                  <button className="text-gray-400 hover:text-brand-500 transition-colors" aria-label="Voice" data-id="element-1026">
                    <Mic className="w-4 h-4" data-id="element-1027" />
                  </button>
                  <button onClick={handleSendText} disabled={!input.trim() || isDone} className="w-8 h-8 rounded-full orange-gradient-bg text-white flex items-center justify-center disabled:opacity-40 transition-opacity" aria-label="Send" data-id="element-1028">
                    <Send className="w-3.5 h-3.5" data-id="element-1029" />
                  </button>
                </div>
                {!isDone && activeSlot && <p className="text-[10px] text-gray-400 mt-2 text-center font-medium tracking-wide" data-id="element-1030">
                    Pair2 AI is asking about: {activeSlot.label}
                  </p>}
              </div>
            </div>
          </div>

          {/* RIGHT PANEL — Booking so far OR full summary */}
          <div className="lg:col-span-2" data-id="element-1031">
            <AnimatePresence mode="wait" data-id="element-1032">
              {!isDone ? <motion.div key="collecting" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sticky top-24" data-id="element-1033">
                  <div className="flex items-center justify-between mb-5" data-id="element-1034">
                    <h3 className="font-bold text-gray-900" data-id="element-1035">Booking so far</h3>
                    <Loader2 className="w-4 h-4 text-brand-500 animate-spin" data-id="element-1036" />
                  </div>

                  <div className="space-y-2.5" data-id="element-1037">
                    {slots.map(slot => {
                  const v = values[slot.id];
                  const filled = v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
                  const isActive = activeSlot?.id === slot.id;
                  return <div key={slot.id} className={`flex items-center justify-between px-4 py-3 rounded-2xl border transition-all ${filled ? 'border-green-100 bg-green-50/40' : isActive ? 'border-brand-300 bg-brand-50/50 shadow-sm' : 'border-gray-100 bg-gray-50/40'}`} data-id="element-1038">
                          <div className="flex items-center gap-2.5" data-id="element-1039">
                            {filled ? <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-1040" /> : isActive ? <div className="w-4 h-4 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" data-id="element-1041" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-200" data-id="element-1042" />}
                            <div data-id="element-1043">
                              <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wide leading-tight" data-id="element-1044">
                                {slot.label}
                                {slot.optional && <span className="ml-1 text-gray-400 normal-case font-normal" data-id="element-1045">
                                    (optional)
                                  </span>}
                              </p>
                              <p className={`text-sm font-semibold leading-tight ${filled ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-1046">
                                {filled ? Array.isArray(v) ? `${v.length} photo${v.length === 1 ? '' : 's'}` : String(v) : isActive ? 'Asking now...' : 'Pending'}
                              </p>
                            </div>
                          </div>
                        </div>;
                })}
                  </div>
                </motion.div> : <motion.div key="summary" initial={{
              opacity: 0,
              y: 16,
              scale: 0.98
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: -10
            }} className="space-y-4 sticky top-24" data-id="element-1047">
                  {/* Route card */}
                  <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5" data-id="element-1048">
                    <div className="flex items-center justify-between mb-4" data-id="element-1049">
                      <h3 className="font-bold text-gray-900 text-sm" data-id="element-1050">
                        Booking Summary
                      </h3>
                      <div className="bg-green-50 text-green-700 px-2.5 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1 border border-green-100" data-id="element-1051">
                        <CheckCircle2 className="w-3 h-3" data-id="element-1052" />
                        Confirmed
                      </div>
                    </div>

                    <div className="space-y-3" data-id="element-1053">
                      {slots.filter(s => values[s.id] !== undefined && values[s.id] !== '').map(s => <div key={s.id} className="flex items-start gap-3 text-sm" data-id="element-1054">
                            <div className="w-7 h-7 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0" data-id="element-1055">
                              {s.kind === 'location' ? <MapPin className="w-3.5 h-3.5" data-id="element-1056" /> : s.kind === 'when' ? <Clock className="w-3.5 h-3.5" data-id="element-1057" /> : <Sparkles className="w-3.5 h-3.5" data-id="element-1058" />}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-1059">
                              <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wide leading-tight" data-id="element-1060">
                                {s.label}
                              </p>
                              <p className="text-sm font-semibold text-gray-900 leading-tight truncate" data-id="element-1061">
                                {Array.isArray(values[s.id]) ? `${(values[s.id] as string[]).length} photos attached` : String(values[s.id])}
                              </p>
                            </div>
                          </div>)}
                    </div>
                  </div>

                  {/* AI recommendation card */}
                  <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5" data-id="element-1062">
                    <h3 className="font-bold text-gray-900 text-sm mb-3 flex items-center gap-2" data-id="element-1063">
                      <Sparkles className="w-4 h-4 text-brand-500" data-id="element-1064" />
                      AI Recommendation
                    </h3>
                    <div className="grid grid-cols-2 gap-2" data-id="element-1065">
                      <div className="p-3 rounded-xl border border-brand-100 bg-brand-50/40" data-id="element-1066">
                        <div className="flex items-center gap-1.5 mb-1" data-id="element-1067">
                          <Truck className="w-3.5 h-3.5 text-brand-500" data-id="element-1068" />
                          <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wide" data-id="element-1069">
                            Vehicle
                          </p>
                        </div>
                        <p className="text-xs font-bold text-gray-900 leading-tight" data-id="element-1070">
                          {service.summary.vehicle}
                        </p>
                      </div>
                      <div className="p-3 rounded-xl border border-gray-100 bg-gray-50/60" data-id="element-1071">
                        <div className="flex items-center gap-1.5 mb-1" data-id="element-1072">
                          <Users className="w-3.5 h-3.5 text-gray-500" data-id="element-1073" />
                          <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wide" data-id="element-1074">
                            {service.summary.crewLabel}
                          </p>
                        </div>
                        <p className="text-xs font-bold text-gray-900 leading-tight" data-id="element-1075">
                          {service.summary.crewValue}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Price + CTA */}
                  <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5" data-id="element-1076">
                    <div className="flex items-end justify-between mb-3" data-id="element-1077">
                      <div data-id="element-1078">
                        <p className="text-xs text-gray-500" data-id="element-1079">Estimated</p>
                        <p className="text-3xl font-display font-bold text-gray-900" data-id="element-1080">
                          AED {service.summary.total}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 pb-1" data-id="element-1081">
                        ~ AED {service.summary.totalUpper}
                      </p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-3 mb-4 flex gap-2" data-id="element-1082">
                      <Shield className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" data-id="element-1083" />
                      <p className="text-[11px] text-blue-800 leading-relaxed" data-id="element-1084">
                        {service.summary.insuranceNote}
                      </p>
                    </div>
                    <button onClick={() => {
                  const photos = Array.isArray(values.photos) ? values.photos as string[] : [];
                  // Persist photos for refresh resilience across the rest of the flow.
                  try {
                    sessionStorage.setItem('pair2.lastPhotos', JSON.stringify(photos));
                  } catch {
                    // ignore
                  }
                  saveConversation(turns);
                  navigate('/results', {
                    state: {
                      service: service.id,
                      prompt: initialPrompt,
                      photos
                    }
                  });
                }} className="w-full py-3.5 bg-brand-500 hover:bg-brand-600 text-white rounded-2xl font-bold text-base transition-colors shadow-md shadow-brand-500/20 flex items-center justify-center gap-2" data-id="element-1085">
                      {service.summary.primaryAction}{' '}
                      <ChevronRight className="w-5 h-5" data-id="element-1086" />
                    </button>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>;
}
// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------
function TurnBubble({
  turn,
  onChip,
  onUpload,
  onSkipPhotos,
  isLatest
}: {
  turn: ConversationTurn;
  onChip: (chip: string) => void;
  onUpload: () => void;
  onSkipPhotos: () => void;
  isLatest: boolean;
}) {
  if (turn.role === 'user') {
    return <motion.div initial={{
      opacity: 0,
      x: 12
    }} animate={{
      opacity: 1,
      x: 0
    }} className="flex justify-end" data-id="element-1087">
        <div className="max-w-[80%]" data-id="element-1088">
          {turn.text && <div className="bg-gray-900 text-white text-sm px-4 py-2.5 rounded-2xl rounded-br-md leading-relaxed" data-id="element-1089">
              {turn.text}
            </div>}
          {turn.attachments && <div className="flex gap-2 mt-1.5 justify-end flex-wrap" data-id="element-1090">
              {turn.attachments.map((att, i) => <div key={i} className="relative w-24 h-20 rounded-xl overflow-hidden border border-gray-200 shadow-sm" data-id="element-1091">
                  <img src={att.url} alt={att.name} className="w-full h-full object-cover" data-id="element-1092" />
                </div>)}
            </div>}
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} className="flex gap-2.5" data-id="element-1093">
      <div className="w-8 h-8 rounded-full orange-gradient-bg flex items-center justify-center shrink-0 mt-0.5 shadow-sm" data-id="element-1094">
        <Sparkles className="w-4 h-4 text-white" data-id="element-1095" />
      </div>
      <div className="flex-1 max-w-[85%]" data-id="element-1096">
        {turn.text && <div className={`text-sm px-4 py-2.5 rounded-2xl rounded-tl-md leading-relaxed whitespace-pre-line ${turn.isComplete ? 'bg-brand-50 border border-brand-100 text-brand-900 font-medium' : 'bg-white border border-gray-100 text-gray-800 shadow-sm'}`} data-id="element-1097">
            {turn.text}
          </div>}

        {/* Photo uploader */}
        {turn.showUploader && isLatest && <div className="mt-2 space-y-2" data-id="element-1098">
            <div className="grid grid-cols-2 gap-2" data-id="element-1099">
              <button onClick={onUpload} className="flex flex-col items-center gap-1.5 p-3 border-2 border-dashed border-brand-200 hover:border-brand-400 bg-brand-50/40 hover:bg-brand-50 rounded-2xl transition-colors text-brand-700" data-id="element-1100">
                <ImagePlus className="w-5 h-5" data-id="element-1101" />
                <span className="text-[11px] font-semibold" data-id="element-1102">Upload photo</span>
              </button>
              <button onClick={onUpload} className="flex flex-col items-center gap-1.5 p-3 border-2 border-dashed border-gray-200 hover:border-gray-300 bg-gray-50/60 hover:bg-gray-100 rounded-2xl transition-colors text-gray-700" data-id="element-1103">
                <Camera className="w-5 h-5" data-id="element-1104" />
                <span className="text-[11px] font-semibold" data-id="element-1105">Take photo</span>
              </button>
            </div>
            <button onClick={onSkipPhotos} className="w-full py-2 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors" data-id="element-1106">
              Skip photos — use standard estimate
            </button>
          </div>}

        {/* Suggestion chips removed — let the AI conversation feel open-ended
            without preset replies. */}
      </div>
    </motion.div>;
}
// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------
// Pick a phrasing for asking about a slot (rotate if there are several variations).
function pickAsk(slot: Slot): string {
  if (!slot.askPhrases.length) return `What's the ${slot.label.toLowerCase()}?`;
  return slot.askPhrases[Math.floor(Math.random() * slot.askPhrases.length)];
}